/**
 * Active Dossiers — Section 5.5 + 5.6 of the architecture blueprint (wired 5.6).
 *
 * Three tabs:
 *   All open          — PENDING/IN_PROGRESS dossiers with slot progress,
 *                       manual remind, cadence override, fresh signed link,
 *                       and an expandable slot checklist.
 *   Pending review    — MED-band classifier output (CLASSIFIED_PENDING_REVIEW):
 *                       approve the guess or re-label.
 *   Declared mismatch — DECLARED_MISMATCH docs: confirm the declared type or
 *                       re-label with the classifier's counter-proposal.
 *
 * Approve/re-label both ride POST /document/documents/{id}/classification —
 * human word is final and triggers a pipeline re-run (re-extract + slot match).
 */

import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiClientError, dossierService } from "@re-platform/api-client";
import type { DossierSummary } from "@re-platform/types";
import { Button, Card, StatusPill, Toast } from "@re-platform/ui";

import {
  actingClientFor,
  brokerageLabel,
  useMergedQuery,
  type Scoped,
} from "../../lib/mergedQuery";
import { useMemberships } from "../../lib/memberships";

const OPEN_STATUSES = new Set(["PENDING", "IN_PROGRESS"]);

export function Active() {
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();

  // The list merges across brokerages for dual-licensed staff (PR 5) — a
  // dossier is the same job whichever side of the business it arrived on.
  const memberships = useMemberships();
  const tenants = memberships.data ?? [];
  const enabled = { enabled: tenants.length > 0 };

  const dossiersQuery = useMergedQuery<DossierSummary>(
    tenants,
    ["dossiers"],
    (client) => dossierService.list(client, { limit: 200 }),
    enabled,
  );

  const openDossiers = dossiersQuery.rows.filter((d) =>
    OPEN_STATUSES.has(d.row.status),
  );

  async function run(work: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await work();
      setToast(done);
      await queryClient.invalidateQueries();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Request failed");
      // A 409 means the row changed under the agent (spec 019: the client
      // replaced the document) — show what is there now.
      if (err instanceof ApiClientError && err.status === 409)
        await queryClient.invalidateQueries();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Active dossiers</h1>
      <p style={{ color: "var(--rp-color-text-muted)" }}>
        Chase missing documents, tune reminder cadence, and review uploaded
        documents.
      </p>

      <DossierList
        dossiers={openDossiers}
        loading={dossiersQuery.isPending}
        error={dossiersQuery.error?.message ?? null}
        isDual={dossiersQuery.isDual}
        busy={busy}
        onRemind={(id, tenantId) =>
          void run(async () => {
            const client = actingClientFor(tenantId, dossiersQuery.isDual);
            return await dossierService.remind(client, id);
          }, "Reminder sent.")
        }
        onCadence={(id, override, tenantId) =>
          void run(
            () =>
              dossierService.putReminderConfig(
                actingClientFor(tenantId, dossiersQuery.isDual),
                id,
                override,
              ),
            override
              ? "Cadence override saved."
              : "Cadence reset to template default.",
          )
        }
        onLink={(id, tenantId) =>
          void run(async () => {
            const link = await dossierService.mintSignedLink(
              actingClientFor(tenantId, dossiersQuery.isDual),
              id,
            );
            await navigator.clipboard.writeText(link.url);
          }, "Fresh 24h link copied to clipboard.")
        }
        onSlotDecision={(
          id,
          templateItemId,
          decision,
          reason,
          tenantId,
          documentId,
        ) =>
          void run(
            () =>
              dossierService.decideSlot(
                actingClientFor(tenantId, dossiersQuery.isDual),
                id,
                templateItemId,
                {
                  decision,
                  ...(reason ? { reason } : {}),
                  // Spec 019: name the document on screen, so an approval
                  // cannot land on a replacement the agent never opened.
                  ...(documentId ? { document_id: documentId } : {}),
                },
              ),
            decision === "approve"
              ? "Approved."
              : "Rejected — the client has been asked to re-upload.",
          )
        }
      />

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}

// --- dossier list -------------------------------------------------------------

/** "Jul 31" — the compact due-date the card meta line prints. */
function fmtDue(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function DossierList({
  dossiers,
  loading,
  error,
  isDual,
  busy,
  onRemind,
  onCadence,
  onLink,
  onSlotDecision,
}: {
  dossiers: readonly Scoped<DossierSummary>[];
  loading: boolean;
  error: string | null;
  /** Caller belongs to >1 brokerage — badge the card meta line. */
  isDual: boolean;
  busy: boolean;
  // Every action carries the tenant of the row it acts on: the list is merged,
  // so the acting tenant cannot be inferred from the sidebar.
  onRemind: (dossierId: string, tenantId: string) => void;
  onCadence: (
    dossierId: string,
    override: Record<string, unknown> | null,
    tenantId: string,
  ) => void;
  onLink: (dossierId: string, tenantId: string) => void;
  onSlotDecision: (
    dossierId: string,
    templateItemId: string,
    decision: "approve" | "reject",
    reason: string | undefined,
    tenantId: string,
    documentId: string | null,
  ) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [cadenceFor, setCadenceFor] = useState<string | null>(null);
  const [cadenceJson, setCadenceJson] = useState("");

  if (loading)
    return (
      <Card padding="lg">
        <p style={{ margin: 0 }}>Loading…</p>
      </Card>
    );
  if (error) {
    return (
      <Card padding="lg">
        <p style={{ margin: 0 }}>
          Couldn't load dossiers: {error}. Is the session token set (sidebar)
          and staff-roled?
        </p>
      </Card>
    );
  }
  if (dossiers.length === 0) {
    return (
      <Card padding="lg">
        <p style={{ margin: 0, color: "var(--rp-color-text-muted)" }}>
          No open dossiers. Create one via POST /document/dossiers (agent UI
          lands with the client-detail screen).
        </p>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {dossiers.map(({ row: dossier, tenant }) => {
        // Expansion/cadence state is keyed per ROW, and row ids are only
        // unique within a tenant — so the key must carry the tenant too,
        // or expanding one brokerage's dossier could expand the other's.
        const rowKey = `${tenant.tenant_id}:${dossier.id}`;
        return (
          <DossierCard
            key={rowKey}
            dossier={dossier}
            tenantId={tenant.tenant_id}
            isDual={isDual}
            brokerage={brokerageLabel(tenant)}
            busy={busy}
            expanded={expanded === rowKey}
            onToggle={() => setExpanded(expanded === rowKey ? null : rowKey)}
            onRemind={() => onRemind(dossier.id, tenant.tenant_id)}
            onLink={() => onLink(dossier.id, tenant.tenant_id)}
            cadenceOpen={cadenceFor === rowKey}
            onCadenceOpen={() => {
              setCadenceFor(cadenceFor === rowKey ? null : rowKey);
              setCadenceJson('{"24h": null}');
            }}
            cadenceJson={cadenceJson}
            setCadenceJson={setCadenceJson}
            onCadenceSave={(override) => {
              onCadence(dossier.id, override, tenant.tenant_id);
              setCadenceFor(null);
            }}
            onSlotDecision={(templateItemId, decision, reason, documentId) =>
              onSlotDecision(
                dossier.id,
                templateItemId,
                decision,
                reason,
                tenant.tenant_id,
                documentId,
              )
            }
          />
        );
      })}
    </div>
  );
}

function DossierCard({
  dossier,
  tenantId,
  isDual,
  brokerage,
  busy,
  expanded,
  onToggle,
  onRemind,
  onLink,
  cadenceOpen,
  onCadenceOpen,
  cadenceJson,
  setCadenceJson,
  onCadenceSave,
  onSlotDecision,
}: {
  dossier: DossierSummary;
  tenantId: string;
  isDual: boolean;
  brokerage: string;
  busy: boolean;
  expanded: boolean;
  onToggle: () => void;
  onRemind: () => void;
  onLink: () => void;
  cadenceOpen: boolean;
  onCadenceOpen: () => void;
  cadenceJson: string;
  setCadenceJson: (value: string) => void;
  onCadenceSave: (override: Record<string, unknown> | null) => void;
  //: V074 — approve/reject a slot awaiting human confirmation.
  onSlotDecision: (
    templateItemId: string,
    decision: "approve" | "reject",
    reason: string | undefined,
    documentId: string | null,
  ) => void;
}) {
  const detailQuery = useQuery({
    // Tenant in the key AND in the request: two brokerages' dossiers share a
    // cache otherwise, and the fetch itself must be addressed to the right one.
    queryKey: ["dossier-detail", tenantId, dossier.id],
    queryFn: () =>
      dossierService.get(actingClientFor(tenantId, isDual), dossier.id),
    enabled: expanded,
  });
  const [cadenceError, setCadenceError] = useState<string | null>(null);

  const template =
    dossier.template_display_name ?? dossier.template_key ?? "Dossier";
  // "Buyer representation · due Jul 31 · Lone Star" — the document count now
  // lives on the progress bar below, so it's dropped from the meta line.
  const meta = [
    template,
    dossier.due_date ? `due ${fmtDue(dossier.due_date)}` : null,
    isDual ? brokerage : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const pct =
    dossier.slots_total > 0
      ? Math.round((dossier.slots_validated / dossier.slots_total) * 100)
      : 0;
  // Amber while the client hasn't really started (PENDING), brand navy once
  // documents are coming in — the two bar colours in the design.
  const barColor =
    dossier.status === "PENDING"
      ? "var(--rp-color-warning)"
      : "var(--rp-color-primary)";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: "16px 18px",
        border: "1px solid var(--rp-color-border)",
        borderRadius: "var(--rp-radius-lg)",
        background: "var(--rp-color-surface)",
        boxShadow: "var(--rp-shadow-sm)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Link
              to={`/clients/${dossier.client_id}`}
              style={{
                fontWeight: 700,
                fontSize: 16,
                color: "var(--rp-color-text)",
              }}
              title="Open this client's uploaded documents"
            >
              {dossier.client_name ?? "—"}
            </Link>
            <StatusPill
              tone={dossier.status === "IN_PROGRESS" ? "info" : "neutral"}
            >
              {dossier.status.toLowerCase().replace("_", " ")}
            </StatusPill>
          </div>
          <div
            style={{
              color: "var(--rp-color-text-muted)",
              fontSize: 13.5,
              marginTop: 2,
            }}
          >
            {meta}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <Button variant="ghost" size="sm" disabled={busy} onClick={onRemind}>
            Remind
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={onCadenceOpen}
          >
            Cadence
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={onLink}>
            Copy link
          </Button>
          <Button variant="ghost" size="sm" onClick={onToggle}>
            {expanded ? "Hide" : "Slots"}
          </Button>
        </div>
      </div>

      {/* Document-collection progress — filled bar + validated/total count. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            flex: 1,
            height: 6,
            borderRadius: 999,
            background: "var(--rp-color-surface-2)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              borderRadius: 999,
              background: barColor,
            }}
          />
        </div>
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--rp-color-text-muted)",
            fontVariantNumeric: "tabular-nums",
            minWidth: 40,
            textAlign: "right",
          }}
        >
          {dossier.slots_validated}/{dossier.slots_total}
        </span>
      </div>

      {cadenceOpen && (
        <div
          style={{
            borderTop: "1px solid var(--rp-color-border)",
            paddingTop: 12,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <code style={{ fontSize: 12 }}>reminder_config_override</code>
            <input
              style={{
                flex: 1,
                padding: "6px 8px",
                border: "1px solid var(--rp-color-border)",
                borderRadius: "var(--rp-radius-sm)",
                font: "inherit",
                fontSize: 13,
              }}
              value={cadenceJson}
              onChange={(e) => setCadenceJson(e.target.value)}
              placeholder='{"24h": null, "72h": "48h"} — null value disables a step'
            />
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                try {
                  const parsed = JSON.parse(cadenceJson) as Record<
                    string,
                    unknown
                  >;
                  setCadenceError(null);
                  onCadenceSave(parsed);
                } catch {
                  setCadenceError("Not valid JSON");
                }
              }}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => onCadenceSave(null)}
            >
              Reset to default
            </Button>
          </div>
          {cadenceError && (
            <p
              style={{
                color: "var(--rp-color-danger)",
                fontSize: 12.5,
                margin: "6px 0 0",
              }}
            >
              {cadenceError}
            </p>
          )}
        </div>
      )}

      {expanded && (
        <div
          style={{
            borderTop: "1px solid var(--rp-color-border)",
            paddingTop: 12,
          }}
        >
          {detailQuery.isPending && <span>Loading slots…</span>}
          {detailQuery.data && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {detailQuery.data.slots.map((slot) => (
                <span
                  key={slot.id}
                  title={slot.rejection_reason ?? undefined}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <StatusPill
                    tone={
                      slot.status === "VALIDATED" ||
                      slot.status === "AGENT_APPROVED"
                        ? "success"
                        : slot.status === "REJECTED"
                          ? "danger"
                          : slot.status === "AWAITING_APPROVAL"
                            ? "warning"
                            : slot.document_id
                              ? "info"
                              : "neutral"
                    }
                  >
                    {slot.display_name}
                  </StatusPill>
                  {/* V074 — nothing CONFIRMED this is the right kind of
                      document (the guard only flags a HIGH-confidence
                      disagreement, so an uncertain verdict lands here). It
                      satisfies nothing until a human decides. */}
                  {slot.status === "AWAITING_APPROVAL" && (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        title="This document satisfies the requirement"
                        onClick={() =>
                          onSlotDecision(
                            slot.template_item_id,
                            "approve",
                            undefined,
                            slot.document_id,
                          )
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        title="Wrong document — ask the client to re-upload"
                        onClick={() => {
                          const reason = window.prompt(
                            "What should the client send instead?",
                            "That isn't the right document for this requirement.",
                          );
                          if (reason === null) return;
                          onSlotDecision(
                            slot.template_item_id,
                            "reject",
                            reason,
                            slot.document_id,
                          );
                        }}
                      >
                        Re-upload
                      </Button>
                    </>
                  )}
                  {/* A Layer-3 cross-document conflict. Some of these are
                      explainable — a maiden name, an employer mid-acquisition
                      — and until now nothing could move a REJECTED slot, so
                      the client just re-uploaded a document that was rejected
                      again. The agent sees what the check found (below) and
                      can accept it on the record. */}
                  {slot.status === "REJECTED" && slot.rejection_reason && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      title="Accept this document despite the conflict"
                      onClick={() => {
                        const reason = window.prompt(
                          `This was rejected because:\n\n${slot.rejection_reason}\n\nWhy is it acceptable anyway? (recorded)`,
                          "",
                        );
                        if (reason === null || !reason.trim()) return;
                        onSlotDecision(
                          slot.template_item_id,
                          "approve",
                          reason.trim(),
                          slot.document_id,
                        );
                      }}
                    >
                      Accept anyway
                    </Button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
